import { css, CSSResult, unsafeCSS } from 'lit'
import { csBorder } from '@cloudsuite/mosaic-shared/styles/globals.style.js'
import { csColorBorder } from '@cloudsuite/mosaic-shared/styles/colors.style.js'
import { csSpacing } from '@cloudsuite/mosaic-shared/styles/spacing.style.js'

export const cseImage = {
  width: css`var(--cse-image-width, auto)`,
  maxWidth: css`var(--cse-image-max-width, auto)`,
  height: css`var(--cse-image-height, auto)`,
  maxHeight: css`var(--cse-image-max-height, auto)`,
  borderRadius: css`var(--cse-image-border-radius, ${csBorder.radiusNone})`,
  borderWidth: css`var(--cse-image-border-width, 0px)`,
  borderStyle: css`var(--cse-image-border-style, solid)`,
  borderColor: css`var(--cse-image-border-color, ${csColorBorder.light})`,
  aspectRatio: css`var(--cse-image-aspect-ratio, auto)`,
}

// The aspectRatio token's own name, read from it so it's written once. The fill-height rule needs the
// name rather than the token, because the token falls back to auto and that rule needs 100%
const aspectRatioName = /^var\((--[\w-]+)/.exec(cseImage.aspectRatio.cssText)?.[1]
if (!aspectRatioName) throw new Error('cseImage.aspectRatio must start with var(--<name>')
const aspectRatioHook = unsafeCSS(aspectRatioName)

export const style: CSSResult = css`
  .cse-image {
    display: flex;
    border-radius: ${cseImage.borderRadius};
    max-height: ${cseImage.maxHeight};
    max-width: ${cseImage.maxWidth};
  }

  .auto-size {
    width: 100%;
    height: auto;
  }

  .fit-cover,
  .fit-contain {
    width: 100%;
  }

  .fit-cover {
    object-fit: cover;
  }

  .fit-contain {
    object-fit: contain;
    object-position: center;
  }

  .fit-cover,
  .fit-contain {
    height: 100%;
  }

  /* Where the ratio rule below applies, the ratio decides the height instead of the container.
     A ratio such as 1 / 1 is not a valid height, so when --cse-image-aspect-ratio is set this
     declaration drops out (height: auto). Guarded like the ratio rule, so an image with both width
     and height keeps filling its container */
  .fit-cover:not([width][height]),
  .fit-contain:not([width][height]) {
    height: var(${aspectRatioHook}, 100%);
  }

  .fit-fill {
    object-fit: fill;
  }

  .fit-scale-down {
    object-fit: scale-down;
  }

  .grayscale {
    filter: grayscale(1);
  }

  .grayscale:hover {
    filter: grayscale(0);
  }

  .border {
    border-width: ${cseImage.borderWidth};
    border-style: ${cseImage.borderStyle};
    border-color: ${cseImage.borderColor};
  }

  /* --cse-image-aspect-ratio only sizes images whose fit can't stretch them, so a theme value
     never distorts an image. With both width and height attributes the browser derives the ratio
     from them (auto w / h), which reserves space while loading, so it's left alone */
  .fit-cover:not([width][height]),
  .fit-contain:not([width][height]),
  .fit-scale-down:not([width][height]) {
    aspect-ratio: ${cseImage.aspectRatio};
  }

  .caption {
    margin-top: ${csSpacing.spacing04};
    display: block;
    max-width: 100%;
    overflow-wrap: break-word;
    word-break: break-word;
  }
`
